import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useFeatures } from '../../context/FeatureContext';
import { useFeature } from '../../hooks/useFeature';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import DescriptionView from '../../components/DescriptionView';
import ImageLightbox from '../../components/shared/ImageLightbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../../components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import api from '../../services/api';
import PaymentMethodChooser from '../../components/checkout/PaymentMethodChooser';
import PayWithCreditButton from '../../components/parent/PayWithCreditButton';
import { toast } from 'sonner';
import {
  Calendar,
  MapPin,
  DollarSign,
  UserPlus,
  AlertTriangle,
  CheckCircle,
  Clock,
  Trophy,
  Home,
  Plane,
  XCircle,
  HelpCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Users,
  MessageCircle,
  Filter,
  Archive,
  CheckCircle2,
  X,
  Bell,
  BellOff,
} from 'lucide-react';
import TeamChat from '../../components/chat/TeamChat';
import { LocationLink } from '../../components/tournaments/LocationLink';
import { RegistrationsTable } from '../../components/tournaments/RegistrationsTable';
import { ParentTournamentRegisterDialog } from '../../components/tournaments/ParentTournamentRegisterDialog';
import {
  RSVP_OPTIONS,
  RSVP_COLORS,
  statusColors,
} from '../../components/tournaments/constants';
import { useBusinessTimezone, getTodayInTz, formatDateOnlyDisplay } from '../../utils/businessTime';

const ParentTournaments = () => {
  const businessTz = useBusinessTimezone();
  const { tournamentId } = useParams();
  const [searchParams] = useSearchParams();
  const registrationIdFromUrl = searchParams.get('registration_id');
  
  // Feature flags
  const { isFeatureEnabled } = useFeatures();
  const teamChatsFeature = useFeature('comms.team_chats');
  
  const [tournaments, setTournaments] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [dismissedAlertIds, setDismissedAlertIds] = useState([]);
  const [showDismissedAlerts, setShowDismissedAlerts] = useState(false);
  const [myRegistrations, setMyRegistrations] = useState([]);
  const [players, setPlayers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [eligibilityInfo, setEligibilityInfo] = useState(null);
  
  // Game schedule & RSVP state
  const [myTournaments, setMyTournaments] = useState([]);
  const [myTeams, setMyTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [expandedTournament, setExpandedTournament] = useState(null);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [showRsvpDialog, setShowRsvpDialog] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [submittingRsvp, setSubmittingRsvp] = useState(false);
  const [rsvpSelections, setRsvpSelections] = useState({});
  const [showAllRsvpsDialog, setShowAllRsvpsDialog] = useState(false);
  const [allRsvps, setAllRsvps] = useState([]);
  const [loadingRsvps, setLoadingRsvps] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    // Allow deep-linking into a specific tab via ?tab= (e.g. the Games menu
    // item points to /parent/tournaments?tab=schedule).
    const t = searchParams.get('tab');
    return ['registrations', 'schedule', 'my-teams', 'chats'].includes(t) ? t : 'registrations';
  });
  const [gamePlayerFilter, setGamePlayerFilter] = useState('all'); // 'all' or player_id
  const [gameTeamFilter, setGameTeamFilter] = useState('all'); // 'all' or team_id
  const [gameAgeFilter, setGameAgeFilter] = useState('all'); // 'all' or age_category
  const [cancelRegistrationDialogOpen, setCancelRegistrationDialogOpen] = useState(false);
  const [payingRegistration, setPayingRegistration] = useState(null);
  const [registrationToCancel, setRegistrationToCancel] = useState(null);
  const [withdrawalReason, setWithdrawalReason] = useState('');
  const [submittingWithdrawal, setSubmittingWithdrawal] = useState(false);
  
  // Team Chat state
  const [teamChats, setTeamChats] = useState([]);
  const [selectedTeamChat, setSelectedTeamChat] = useState(null);
  const [loadingChats, setLoadingChats] = useState(false);
  
  // Tournament schedule filter - hide past by default
  const [showPastTournaments, setShowPastTournaments] = useState(false);
  
  // Expanded tournament cards in All Tournaments section (collapsed by default)
  const [expandedTournamentCards, setExpandedTournamentCards] = useState(new Set());
  
  const toggleTournamentCard = (tournamentId) => {
    setExpandedTournamentCards(prev => {
      const next = new Set(prev);
      if (next.has(tournamentId)) {
        next.delete(tournamentId);
      } else {
        next.add(tournamentId);
      }
      return next;
    });
  };
  
  const [formData, setFormData] = useState({
    player_id: '',
    age_category: '',
    training_location_id: '',
    division_preference: '',
    can_travel: true,
    availability_status: 'yes',
    available_dates: [],
    unavailable_dates: [],
    notes: '',
    parent_consent: false,
  });

  useEffect(() => {
    fetchData();
  }, [showPastTournaments]);  // Re-fetch when past tournament filter changes

  // Load dismissed alerts from backend
  useEffect(() => {
    const loadDismissed = async () => {
      try {
        const res = await api.get('/notifications/dismissed/tournament_alert');
        setDismissedAlertIds(res.data || []);
      } catch (e) {
        console.error('Failed to load dismissed alerts:', e);
      }
    };
    loadDismissed();
  }, []);

  useEffect(() => {
    // Handle payment confirmation from URL
    const sessionId = searchParams.get('session_id');
    if (sessionId && registrationIdFromUrl && tournamentId) {
      confirmPayment(tournamentId, sessionId, registrationIdFromUrl);
    }
  }, [searchParams, tournamentId, registrationIdFromUrl]);

  const fetchData = async () => {
    try {
      const [tournamentsRes, alertsRes, registrationsRes, playersRes, myTournamentsRes, locationsRes] = await Promise.all([
        api.get('/tournaments'),
        api.get('/tournaments/alerts/active'),
        api.get('/tournaments/my-registrations'),
        api.get('/players/my-children'),  // Use my-children endpoint to get only user's children
        api.get(`/tournaments/my-tournaments?include_past=${showPastTournaments}`),
        api.get('/locations/active')
      ]);
      // Sort tournaments by start_date (earliest first)
      const sortedTournaments = [...tournamentsRes.data].sort((a, b) => 
        new Date(a.start_date) - new Date(b.start_date)
      );
      setTournaments(sortedTournaments);
      setAlerts(alertsRes.data);
      setMyRegistrations(registrationsRes.data);
      setPlayers(playersRes.data);
      // Sort my tournaments by start_date (earliest first)
      const sortedMyTournaments = [...myTournamentsRes.data].sort((a, b) => 
        new Date(a.start_date) - new Date(b.start_date)
      );
      setMyTournaments(sortedMyTournaments);
      setLocations(locationsRes.data || []);

      // Tournaments stay collapsed by default — user expands the one they
      // want. This keeps the Games tab (deep-linked from the sidebar) scannable
      // when the parent has several tournaments on their plate.
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch team chats
  const fetchTeamChats = async () => {
    try {
      setLoadingChats(true);
      const response = await api.get('/chat/teams');
      setTeamChats(response.data);
    } catch (error) {
      console.error('Failed to load team chats:', error);
    } finally {
      setLoadingChats(false);
    }
  };

  // Fetch my teams
  const fetchMyTeams = async () => {
    try {
      setLoadingTeams(true);
      const response = await api.get('/tournaments/my-teams/all');
      setMyTeams(response.data.teams || []);
    } catch (error) {
      console.error('Failed to load my teams:', error);
    } finally {
      setLoadingTeams(false);
    }
  };

  // Load team chats when switching to chat tab OR on initial load
  useEffect(() => {
    if (teamChatsFeature.enabled) {
      fetchTeamChats();
    }
  }, [teamChatsFeature.enabled]);
  
  useEffect(() => {
    if (!teamChatsFeature.enabled && activeTab === 'chats') {
      setActiveTab('registrations');
      return;
    }
    if (activeTab === 'chats' && teamChatsFeature.enabled) {
      fetchTeamChats();
    }
    if (activeTab === 'my-teams') {
      fetchMyTeams();
    }
  }, [activeTab]);

  const confirmPayment = async (tId, sessionId, regId) => {
    try {
      const res = await api.post(`/tournaments/${tId}/payment/confirm?session_id=${sessionId}&registration_id=${regId}`);
      if (res.data.status === 'confirmed') {
        toast.success('Payment confirmed! Your spot is reserved.');
      } else if (res.data.status === 'processing') {
        toast.success(res.data.message || 'Your bank payment is processing and will be confirmed within a few business days.');
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to confirm payment');
    }
  };

  const openRegisterDialog = (tournament) => {
    setSelectedTournament(tournament);
    setEligibilityInfo(null);
    // Division preference only for local (non-travel) tournaments
    // Travel tournaments have no divisions
    const isTravel = tournament.location_type === 'out_of_state';
    const isLeague = tournament.tournament_style === 'league';
    const defaultDivision = (isTravel || isLeague) ? '' : 
                           tournament.tournament_style === 'competition' ? 'div_a' : 
                           tournament.tournament_style === 'recreational' ? 'div_b' : '';
    setFormData({
      player_id: '',
      age_category: tournament.age_categories[0] || '',
      play_up: false,
      play_up_category: '',
      training_location_id: '',
      division_preference: defaultDivision,
      can_travel: true,
      availability_status: 'yes',
      available_dates: [],
      unavailable_dates: [],
      notes: '',
      parent_consent: false,
    });
    setShowRegisterDialog(true);
  };

  // Dismiss tournament alert (persist to backend)
  const dismissAlert = async (tournamentId) => {
    try {
      await api.post(`/notifications/tournament_alert/${tournamentId}/dismiss`);
      setDismissedAlertIds(prev => [...prev, tournamentId]);
      toast.success('Alert dismissed.');
    } catch (e) {
      toast.error('Failed to dismiss alert');
    }
  };

  // Restore dismissed alert
  const restoreAlert = async (tournamentId) => {
    try {
      await api.delete(`/notifications/tournament_alert/${tournamentId}/dismiss`);
      setDismissedAlertIds(prev => prev.filter(id => id !== tournamentId));
      toast.success('Alert restored.');
    } catch (e) {
      toast.error('Failed to restore alert');
    }
  };

  // Get visible alerts (not dismissed)
  const visibleAlerts = alerts.filter(alert => !dismissedAlertIds.includes(alert.tournament_id));
  
  // Get dismissed alerts
  const dismissedAlerts = alerts.filter(alert => dismissedAlertIds.includes(alert.tournament_id));

  const checkEligibility = async (playerId, tournament) => {
    if (!playerId || !tournament) return;
    try {
      const res = await api.get(`/tournaments/${tournament.id}/eligibility/${playerId}`);
      setEligibilityInfo(res.data);

      // Auto-select the recommended age category
      if (res.data.eligibility && res.data.eligibility.length > 0) {
        const eligible = res.data.eligibility.find(e => e.is_eligible);
        if (eligible) {
          setFormData(prev => ({ ...prev, age_category: eligible.category }));
        }
      }
    } catch (error) {
      // Surface actionable errors (e.g. missing DOB) to the parent instead of
      // silently hiding the panel — otherwise they hit submit and see the
      // same error after filling the form.
      const detail = error?.response?.data?.detail;
      if (detail) {
        toast.error(detail);
      }
      setEligibilityInfo(null);
    }
  };

  // Get the play-up category (one category higher than eligible)
  const getPlayUpCategory = () => {
    if (!eligibilityInfo || !formData.age_category) return null;
    
    const categoryOrder = ['u7', 'u9', 'u11', 'u13', 'u15', 'u17', 'u19', 'open'];
    const currentIndex = categoryOrder.indexOf(formData.age_category);
    
    if (currentIndex === -1 || currentIndex >= categoryOrder.length - 1) return null;
    
    const nextCategory = categoryOrder[currentIndex + 1];
    
    // Check if the next category is available in this tournament
    if (selectedTournament?.age_categories?.includes(nextCategory)) {
      return nextCategory;
    }
    return null;
  };

  // Check eligibility when player is selected
  const handlePlayerSelect = (playerId) => {
    setFormData({ ...formData, player_id: playerId });
    if (playerId && selectedTournament) {
      checkEligibility(playerId, selectedTournament);
    } else {
      setEligibilityInfo(null);
    }
  };

  // Get eligibility for selected age category
  const getEligibilityForCategory = (category) => {
    if (!eligibilityInfo) return null;
    return eligibilityInfo.eligibility?.find(e => e.category === category);
  };

  const handleRegister = async () => {
    if (!formData.parent_consent) {
      toast.error('Please provide parent consent to register');
      return;
    }
    
    if (!formData.training_location_id) {
      toast.error('Please select your training location');
      return;
    }
    
    try {
      // If play_up is checked, register for BOTH the base category AND the play-up category
      const categoriesToRegister = [];
      
      // Always register for the base (selected) category
      categoriesToRegister.push(formData.age_category);
      
      // If play-up is checked and a play-up category is selected, also register for that
      if (formData.play_up && formData.play_up_category && formData.play_up_category !== formData.age_category) {
        categoriesToRegister.push(formData.play_up_category);
      }
      
      let successCount = 0;
      let errorMessages = [];
      const createdRegs = [];

      for (const category of categoriesToRegister) {
        try {
          const submitData = {
            ...formData,
            division_preference: formData.division_preference || null,
            age_category: category,
            play_up: category !== formData.age_category, // Mark as play_up if not the base category
            original_age_category: formData.age_category,
            training_location_id: formData.training_location_id,
          };
          
          const resp = await api.post(`/tournaments/${selectedTournament.id}/register`, submitData);
          createdRegs.push(resp.data);
          successCount++;
        } catch (error) {
          const detail = error.response?.data?.detail;
          if (typeof detail === 'string' && detail.includes('already registered')) {
            // Skip if already registered for this category
            errorMessages.push(`${category.toUpperCase()}: Already registered`);
          } else {
            errorMessages.push(`${category.toUpperCase()}: ${detail || 'Failed'}`);
          }
        }
      }
      
      if (successCount > 0) {
        const waitlisted = createdRegs.filter((r) => r?.status === 'waitlisted');
        const needsPayment = createdRegs.find((r) => r?.status === 'payment_pending');

        setShowRegisterDialog(false);
        setEligibilityInfo(null);

        if (waitlisted.length === createdRegs.length && waitlisted.length > 0) {
          // iter348 — past the deadline: parent joined the waitlist.
          toast.success(
            'Registration is closed, so you\u2019ve been added to the waitlist. '
            + 'The academy will contact you if a spot opens up.',
            { duration: 6000 },
          );
          fetchData();
        } else if (needsPayment) {
          // iter348 — this tournament requires payment to complete the
          // registration. Open the payment dialog straight away.
          toast.info('Almost done — please complete payment to confirm your registration.');
          const regForPay = {
            ...needsPayment,
            amount_due: needsPayment.amount ?? needsPayment.payment_amount,
            initial_payment: selectedTournament.initial_payment_amount,
          };
          fetchData();
          handlePayment(selectedTournament, regForPay);
        } else if (successCount === categoriesToRegister.length) {
          toast.success(`Successfully registered for ${categoriesToRegister.map(c => c.toUpperCase()).join(' and ')}!`);
          fetchData();
        } else {
          toast.success(`Registered for ${successCount} category. ${errorMessages.join('. ')}`);
          fetchData();
        }
      } else {
        toast.error(errorMessages.join('. ') || 'Failed to register');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const errorMsg = detail.map(e => e.msg || e.message || String(e)).join(', ');
        toast.error(errorMsg || 'Failed to register');
      } else if (typeof detail === 'object') {
        toast.error(detail.msg || detail.message || JSON.stringify(detail));
      } else {
        toast.error(detail || 'Failed to register');
      }
    }
  };

  // Two-step payment flow: clicking "Pay" opens a chooser dialog so the
  // parent picks Card vs ACH BEFORE we create the Stripe Checkout session.
  // Replaces the old "show both at Stripe, surcharge always, refund-if-ACH"
  // pattern.
  const [payDialog, setPayDialog] = useState(null);     // {tournament, registration, baseAmount}
  const [payChoice, setPayChoice] = useState(null);

  const handlePayment = (tournament, registration) => {
    setPayChoice(null);
    setPayDialog({
      tournament,
      registration,
      baseAmount: Number(registration?.amount_due ?? registration?.initial_payment ?? tournament?.initial_payment_amount) || 0,
    });
  };

  const handleConfirmPayment = async () => {
    if (!payDialog || !payChoice) return;
    const { tournament, registration } = payDialog;
    setPayingRegistration(registration.id);
    try {
      const returnUrl = encodeURIComponent(window.location.origin + '/parent/tournaments');
      const res = await api.post(
        `/tournaments/${tournament.id}/payment/checkout`
        + `?registration_id=${registration.id}`
        + `&return_url=${returnUrl}`
        + `&payment_method_choice=${payChoice}`,
      );
      if (res.data?.paid_via_credit) {
        toast.success(
          `Paid with account credit · ${cs()}${Number(res.data.credit_applied || res.data.amount || 0).toFixed(2)} deducted`,
        );
        setPayingRegistration(null);
        setPayDialog(null);
        fetchData();
        return;
      }
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start payment');
      setPayingRegistration(null);
    }
  };

  const handleCancelRegistration = async (tournamentId, registrationId, reg) => {
    setRegistrationToCancel({ tournamentId, registrationId, reg });
    setWithdrawalReason('');
    setCancelRegistrationDialogOpen(true);
  };

  const confirmCancelRegistration = async () => {
    if (!registrationToCancel || !withdrawalReason.trim()) {
      toast.error('Please provide a reason for withdrawal');
      return;
    }
    setSubmittingWithdrawal(true);
    try {
      await api.post(
        `/tournaments/${registrationToCancel.tournamentId}/registrations/${registrationToCancel.registrationId}/withdrawal-request`,
        { reason: withdrawalReason.trim() }
      );
      toast.success('Withdrawal request submitted. Administration will review your request.');
      setCancelRegistrationDialogOpen(false);
      setRegistrationToCancel(null);
      setWithdrawalReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit withdrawal request');
    } finally {
      setSubmittingWithdrawal(false);
    }
  };

  // Get unregistered players for a tournament
  const getUnregisteredPlayers = (tournament) => {
    // Get registrations for this tournament grouped by player and age category
    const playerRegistrations = myRegistrations
      .filter(r => r.tournament_id === tournament.id)
      .reduce((acc, r) => {
        if (!acc[r.player_id]) {
          acc[r.player_id] = new Set();
        }
        acc[r.player_id].add(r.age_category);
        return acc;
      }, {});
    
    // A player is available if they haven't registered for ALL available age categories
    // or if they have no registrations at all
    return players.filter(p => {
      const registeredCategories = playerRegistrations[p.id];
      if (!registeredCategories) return true; // Not registered at all
      
      // Check if there are still categories available to register for
      const availableCategories = tournament.age_categories || [];
      const unregisteredCategories = availableCategories.filter(cat => !registeredCategories.has(cat));
      return unregisteredCategories.length > 0;
    });
  };

  // Get categories a player can still register for
  const getAvailableCategoriesForPlayer = (tournament, playerId) => {
    const playerRegs = myRegistrations.filter(r => 
      r.tournament_id === tournament.id && r.player_id === playerId
    );
    const registeredCategories = new Set(playerRegs.map(r => r.age_category));
    return (tournament.age_categories || []).filter(cat => !registeredCategories.has(cat));
  };

  // ==================== GAME SCHEDULE & RSVP FUNCTIONS ====================

  const formatGameDate = (dateStr) => {
    return formatDateOnlyDisplay(dateStr, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: undefined,
    });
  };

  const formatDateRange = (start, end) => {
    const startStr = formatDateOnlyDisplay(start, { month: 'short', day: 'numeric', year: undefined });
    const endStr = formatDateOnlyDisplay(end, { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} - ${endStr}`;
  };

  const openRsvpDialog = (tournament, game) => {
    setSelectedTournament(tournament);
    setSelectedGame(game);
    
    // Pre-populate RSVP selections from existing data
    const selections = {};
    tournament.registered_players?.forEach(player => {
      const existingRsvp = game.my_rsvps?.[player.player_id];
      if (existingRsvp) {
        selections[player.player_id] = existingRsvp;
      }
    });
    setRsvpSelections(selections);
    setShowRsvpDialog(true);
  };

  const handleRsvpSubmit = async () => {
    if (Object.keys(rsvpSelections).length === 0) {
      toast.error('Please select a response for at least one player');
      return;
    }
    
    setSubmittingRsvp(true);
    try {
      const promises = Object.entries(rsvpSelections).map(([playerId, response]) => 
        api.post(`/tournaments/${selectedTournament.id}/games/${selectedGame.id}/rsvp`, {
          player_id: playerId,
          response: response
        })
      );
      
      await Promise.all(promises);
      toast.success('RSVP submitted successfully');
      setShowRsvpDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit RSVP');
    } finally {
      setSubmittingRsvp(false);
    }
  };

  const viewAllRsvps = async (tournament, game) => {
    setSelectedGame(game);
    setSelectedTournament(tournament);
    setLoadingRsvps(true);
    setShowAllRsvpsDialog(true);
    
    try {
      const res = await api.get(`/tournaments/${tournament.id}/games/${game.id}/rsvps`);
      setAllRsvps(res.data);
    } catch (error) {
      toast.error('Failed to load RSVPs');
    } finally {
      setLoadingRsvps(false);
    }
  };

  const getPlayerRsvpForGame = (tournament, game, playerId) => {
    return game.my_rsvps?.[playerId];
  };

  // Shared filter predicate for the Games tab (team / age / player). "Played"
  // is defined exactly as the academy's export/cost logic: a player is counted
  // for a game when their id is in that game's `selected_players` lineup.
  const gameMatchesFilters = (game, tournament) => {
    if (gameTeamFilter !== 'all' && game.team_name !== gameTeamFilter) return false;
    if (gameAgeFilter !== 'all' && game.age_category !== gameAgeFilter) return false;
    if (gamePlayerFilter !== 'all') {
      const sp = tournament.registered_players?.find(p => p.player_id === gamePlayerFilter);
      const cat = (sp?.age_category || '').toLowerCase();
      const gcats = (game.age_categories || [game.age_category]).map(c => (c || '').toLowerCase());
      const sel = game.selected_players || [];
      if (!(sel.includes(gamePlayerFilter) || gcats.includes(cat))) return false;
    }
    return true;
  };

  // Per-child "played Y of X" summary for a tournament. X = games relevant to
  // the child (their age category/team, or any game they were selected for);
  // Y = games where the child is in `selected_players`.
  const getPlayerGameStats = (tournament) => {
    const games = tournament.games || [];
    const seen = new Set();
    return (tournament.registered_players || [])
      .filter(p => { if (seen.has(p.player_id)) return false; seen.add(p.player_id); return true; })
      .map(p => {
        const cat = (p.age_category || '').toLowerCase();
        const relevant = games.filter(g => {
          const sel = g.selected_players || [];
          if (sel.includes(p.player_id)) return true;
          const gcats = (g.age_categories || [g.age_category]).map(c => (c || '').toLowerCase());
          return gcats.includes(cat);
        });
        const played = relevant.filter(g => (g.selected_players || []).includes(p.player_id)).length;
        return { player_id: p.player_id, player_name: p.player_name, total: relevant.length, played };
      });
  };

  const getRsvpIcon = (response) => {
    const option = RSVP_OPTIONS.find(o => o.value === response);
    if (!option) return null;
    const Icon = option.icon;
    return <Icon className={`h-4 w-4 ${option.color}`} />;
  };

  if (loading) {
    return (
      <DashboardLayout title="Tournaments">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Tournaments">
      <div className="space-y-6" data-testid="parent-tournaments">
        {/* Active Tournament Alerts */}
        {visibleAlerts.length > 0 && (
          <div className="space-y-4">
            {visibleAlerts.map(alert => (
              <Alert key={alert.tournament_id} className="border-amber-200 bg-amber-50 relative">
                <button
                  onClick={() => dismissAlert(alert.tournament_id)}
                  className="absolute top-2 right-2 p-1 rounded-full hover:bg-amber-200 transition-colors"
                  title="Dismiss notification"
                  data-testid={`dismiss-alert-${alert.tournament_id}`}
                >
                  <X className="h-4 w-4 text-amber-600" />
                </button>
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <AlertTitle className="text-amber-800 pr-8">
                  Tournament Registration Open: {alert.tournament_name}
                </AlertTitle>
                <AlertDescription className="text-amber-700">
                  <div className="mt-2 space-y-1">
                    <p>Dates: {alert.start_date} to {alert.end_date}</p>
                    {alert.location && <p>Location: {alert.location}</p>}
                    <p>Registration closes: {alert.registration_end_date}</p>
                    <p>Initial payment: {cs()}{alert.initial_payment}</p>
                    <p>Age categories: {alert.age_categories.map(c => c.toUpperCase()).join(', ')}</p>
                    
                    {getUnregisteredPlayers({ id: alert.tournament_id, age_categories: alert.age_categories }).length > 0 && (
                      <Button
                        size="sm"
                        className="mt-3"
                        onClick={() => {
                          const t = tournaments.find(t => t.id === alert.tournament_id);
                          if (t) openRegisterDialog(t);
                        }}
                        data-testid={`register-alert-${alert.tournament_id}`}
                      >
                        <UserPlus className="h-4 w-4 mr-2" />
                        Register Now
                      </Button>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            ))}
          </div>
        )}

        {/* Dismissed Alerts Section */}
        {dismissedAlerts.length > 0 && (
          <div className="border rounded-lg">
            <button
              onClick={() => setShowDismissedAlerts(!showDismissedAlerts)}
              className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="toggle-dismissed-alerts"
            >
              <div className="flex items-center gap-2 text-slate-600">
                <BellOff className="h-4 w-4" />
                <span className="text-sm font-medium">Dismissed Notifications ({dismissedAlerts.length})</span>
              </div>
              {showDismissedAlerts ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            
            {showDismissedAlerts && (
              <div className="p-3 space-y-3 border-t">
                {dismissedAlerts.map(alert => (
                  <div key={alert.tournament_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-slate-700">{alert.tournament_name}</p>
                      <p className="text-sm text-slate-500">
                        {alert.start_date} to {alert.end_date} | Registration closes: {alert.registration_end_date}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const t = tournaments.find(t => t.id === alert.tournament_id);
                          if (t) openRegisterDialog(t);
                        }}
                        data-testid={`register-dismissed-${alert.tournament_id}`}
                      >
                        <UserPlus className="h-4 w-4 mr-1" />
                        Register
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => restoreAlert(alert.tournament_id)}
                        title="Restore to main view"
                        data-testid={`restore-alert-${alert.tournament_id}`}
                      >
                        <Bell className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex w-full max-w-2xl overflow-x-auto">
            <TabsTrigger value="registrations" data-testid="registrations-tab" className="text-xs lg:text-sm whitespace-nowrap">
              <Trophy className="h-4 w-4 mr-1 shrink-0" />
              <span className="hidden lg:inline">Registrations</span>
              <span className="lg:hidden">Regs</span>
            </TabsTrigger>
            <TabsTrigger value="schedule" data-testid="schedule-tab" className="text-xs lg:text-sm whitespace-nowrap">
              <Calendar className="h-4 w-4 mr-1 shrink-0" />
              Games
            </TabsTrigger>
            {isFeatureEnabled('my_teams') && (
              <TabsTrigger value="my-teams" data-testid="my-teams-tab" className="text-xs lg:text-sm whitespace-nowrap">
                <Users className="h-4 w-4 mr-1 shrink-0" />
                Teams
              </TabsTrigger>
            )}
            {isFeatureEnabled('team_chat') && teamChatsFeature.enabled && (
              <TabsTrigger value="chats" data-testid="team-chats-tab" className="relative text-xs lg:text-sm whitespace-nowrap">
                <MessageCircle className="h-4 w-4 mr-1 shrink-0" />
                Chats
                {teamChats.reduce((sum, c) => sum + (c.unread_count || 0), 0) > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] lg:text-xs font-bold rounded-full min-w-[16px] lg:min-w-[18px] h-[16px] lg:h-[18px] flex items-center justify-center px-0.5 lg:px-1">
                    {teamChats.reduce((sum, c) => sum + (c.unread_count || 0), 0) > 99 
                      ? '99+' 
                      : teamChats.reduce((sum, c) => sum + (c.unread_count || 0), 0)}
                  </span>
                )}
              </TabsTrigger>
            )}
          </TabsList>

          {/* Registrations Tab */}
          <TabsContent value="registrations" className="mt-6 space-y-6">

        {/* Filter for past tournaments */}
        <div className="flex items-center justify-end">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={showPastTournaments}
              onChange={(e) => setShowPastTournaments(e.target.checked)}
              className="rounded border-slate-300"
            />
            <span className="text-slate-600">Show Past Tournaments</span>
          </label>
        </div>

        {/* My Registrations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5" />
              My Tournament Registrations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {myRegistrations.length === 0 ? (
              <p className="text-center text-slate-500 py-4">
                No tournament registrations yet
              </p>
            ) : (
              <RegistrationsTable 
                registrations={myRegistrations.filter(reg => {
                  if (showPastTournaments) return true;
                  const tournament = tournaments.find(t => t.id === reg.tournament_id);
                  if (!tournament) return true;
                  return String(tournament.end_date || '') >= getTodayInTz(businessTz);
                })}
                tournaments={tournaments}
                onPayment={handlePayment}
                onCancelRegistration={handleCancelRegistration}
              />
            )}
          </CardContent>
        </Card>

        {/* All Tournaments */}
        <Card>
          <CardHeader>
            <CardTitle>All Tournaments</CardTitle>
          </CardHeader>
          <CardContent>
            {tournaments.filter(t => {
              if (showPastTournaments) return true;
              return String(t.end_date || '') >= getTodayInTz(businessTz);
            }).length === 0 ? (
              <p className="text-center text-slate-500 py-4">
                {showPastTournaments ? 'No tournaments available' : 'No active tournaments. Check "Show Past Tournaments" to see completed ones.'}
              </p>
            ) : (
              <div className="divide-y">
                {[...tournaments]
                  .filter(t => {
                    if (showPastTournaments) return true;
                    return String(t.end_date || '') >= getTodayInTz(businessTz);
                  })
                  .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
                  .map(tournament => {
                  const unregisteredPlayers = getUnregisteredPlayers(tournament);
                  // iter346 — parents can't add new players past the
                  // registration deadline (registration_end_date). The
                  // tournament itself stays active; admins are unaffected.
                  const regEnd = tournament.registration_end_date || '';
                  const registrationClosed = !!regEnd && getTodayInTz(businessTz) > regEnd;
                  // iter348 — once closed, parents can still open the dialog
                  // to JOIN THE WAITLIST (backend creates a waitlisted entry).
                  const canRegister = ['active', 'future'].includes(tournament.status) && unregisteredPlayers.length > 0;
                  const isExpanded = expandedTournamentCards.has(tournament.id);
                  
                  return (
                    <div key={tournament.id} className="py-3 border-b last:border-b-0">
                      {/* Collapsed Header - Always Visible */}
                      <div 
                        className="flex items-center justify-between cursor-pointer hover:bg-slate-50 rounded-lg p-2 -m-2"
                        onClick={() => toggleTournamentCard(tournament.id)}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {/* Expand/Collapse Icon */}
                          <ChevronRight className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          
                          {/* Tournament Thumbnail */}
                          {tournament.image_path && (
                            <ImageLightbox
                              src={`${(typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL)}/api/files/tournament-image/${tournament.id}`}
                              alt={tournament.name}
                              triggerClassName="shrink-0"
                              className="h-10 w-10 rounded-lg object-cover border"
                              testId={`parent-tournament-card-image-${tournament.id}`}
                            />
                          )}
                          
                          {/* Tournament Name & Quick Info */}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold truncate">{tournament.name}</h4>
                              <Badge className={`${statusColors[tournament.status]} text-xs`}>
                                {tournament.status}
                              </Badge>
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {tournament.start_date} to {tournament.end_date} • {tournament.age_categories.map(c => c.toUpperCase()).join(', ')}
                            </div>
                            {/* iter348 — registration deadline banner */}
                            {regEnd && (() => {
                              const today = getTodayInTz(businessTz);
                              const daysLeft = Math.round((new Date(regEnd + 'T00:00:00') - new Date(today + 'T00:00:00')) / 86400000);
                              if (registrationClosed) {
                                return (
                                  <div className="text-[11px] mt-1 inline-flex items-center gap-1 text-amber-700 font-medium" data-testid={`deadline-banner-${tournament.id}`}>
                                    <Clock className="h-3 w-3" /> Registration closed on {regEnd} · waitlist open
                                  </div>
                                );
                              }
                              const urgent = daysLeft <= 3;
                              return (
                                <div className={`text-[11px] mt-1 inline-flex items-center gap-1 font-medium ${urgent ? 'text-red-600' : 'text-emerald-700'}`} data-testid={`deadline-banner-${tournament.id}`}>
                                  <Clock className="h-3 w-3" />
                                  Registration closes {regEnd}
                                  {daysLeft === 0 ? ' · Last day!' : daysLeft === 1 ? ' · 1 day left' : ` · ${daysLeft} days left`}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                        
                        {/* Register / Join Waitlist Button - visible even when collapsed */}
                        {canRegister && (
                          <Button
                            size="sm"
                            variant={registrationClosed ? 'outline' : 'default'}
                            onClick={(e) => {
                              e.stopPropagation();
                              openRegisterDialog(tournament);
                            }}
                            data-testid={`register-${tournament.id}`}
                            title={registrationClosed ? `Registration closed on ${regEnd} — join the waitlist` : undefined}
                          >
                            <UserPlus className="h-4 w-4 mr-1" />
                            {registrationClosed ? 'Join Waitlist' : 'Register'}
                          </Button>
                        )}
                      </div>
                      
                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="mt-3 pl-9 space-y-3">
                          <div className="flex flex-wrap gap-2">
                            <Badge variant="outline">
                              {tournament.location_type === 'out_of_state' ? 'Out of State' : 'Local'}
                            </Badge>
                            <Badge className={
                              tournament.tournament_style === 'competition' ? 'bg-purple-100 text-purple-700' :
                              tournament.tournament_style === 'recreational' ? 'bg-teal-100 text-teal-700' :
                              'bg-indigo-100 text-indigo-700'
                            }>
                              {tournament.tournament_style === 'competition' ? 'Competition' :
                               tournament.tournament_style === 'recreational' ? 'Recreational' : 'League'}
                            </Badge>
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                            {tournament.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-4 w-4" />
                                {tournament.location}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <DollarSign className="h-4 w-4" />
                              {cs()}{tournament.initial_payment_amount} initial
                            </span>
                          </div>

                          <div className="flex flex-wrap gap-1">
                            {tournament.age_categories.map(cat => (
                              <Badge key={cat} variant="secondary" className="text-xs">
                                {cat.toUpperCase()}
                              </Badge>
                            ))}
                          </div>
                          
                          {tournament.details && (
                            <DescriptionView
                              value={tournament.details}
                              className="text-sm text-slate-500"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          {/* Game Schedule Tab */}
          <TabsContent value="schedule" className="mt-6 space-y-4">
            {/* Filter to show/hide past tournaments */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-lg font-semibold text-slate-800">My Tournament Schedule</h2>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showPastTournaments}
                    onChange={(e) => setShowPastTournaments(e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  <span className="text-slate-600">Show Past Tournaments</span>
                </label>
              </div>
            </div>
            
            {myTournaments.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Trophy className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-medium text-slate-900">
                    {showPastTournaments ? 'No Tournaments' : 'No Active Tournaments'}
                  </h3>
                  <p className="text-slate-500">
                    {showPastTournaments 
                      ? 'You are not registered for any tournaments yet.'
                      : 'You don\'t have any active tournaments. Check "Show Past Tournaments" to see completed ones.'}
                  </p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setActiveTab('registrations')}
                  >
                    Browse Tournaments
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {myTournaments.map(tournament => (
                  <Card key={tournament.id} className="overflow-hidden">
                    {/* Tournament Header */}
                    <CardHeader 
                      className="cursor-pointer hover:bg-slate-50 transition-colors p-3 lg:p-6"
                      onClick={() => setExpandedTournament(
                        expandedTournament === tournament.id ? null : tournament.id
                      )}
                    >
                      <div className="flex items-center gap-3">
                        {tournament.image_path ? (
                          <img 
                            src={`${(typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL)}/api/files/tournament-image/${tournament.id}`}
                            alt={tournament.name}
                            className="h-10 w-10 lg:h-14 lg:w-14 rounded-lg object-cover border shrink-0"
                            onError={(e) => { 
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className={`p-2 lg:p-3 bg-emerald-100 rounded-lg shrink-0 ${tournament.image_path ? 'hidden' : ''}`}>
                          <Trophy className="h-5 w-5 lg:h-6 lg:w-6 text-emerald-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <CardTitle className="text-sm lg:text-xl font-bold truncate">{tournament.name}</CardTitle>
                            <div className="flex items-center gap-2 shrink-0">
                              <Badge variant="outline" className="text-xs whitespace-nowrap">
                                {tournament.games?.length || 0} games
                              </Badge>
                              {expandedTournament === tournament.id ? (
                                <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
                              )}
                            </div>
                          </div>
                          <CardDescription className="flex items-center gap-1 mt-0.5">
                            <Calendar className="h-3.5 w-3.5 shrink-0" />
                            <span className="text-xs lg:text-sm whitespace-nowrap">{formatDateRange(tournament.start_date, tournament.end_date)}</span>
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>

                    {/* Expanded Content */}
                    {expandedTournament === tournament.id && (
                      <CardContent className="border-t bg-slate-50/50">
                        {/* Registered Players with Payment Status */}
                        <div className="mb-6">
                          <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            Registered Players
                          </h4>
                          <div className="space-y-2">
                            {tournament.registered_players?.map(player => {
                              const isPaid = player.payment_status === 'paid' || player.status === 'confirmed';
                              const isPartial = player.payment_status === 'partial';
                              const needsPayment = !isPaid && player.status !== 'cancelled';
                              const balanceDue = Number(player.balance_due || 0);
                              const fee = Number(
                                isPartial && balanceDue > 0
                                  ? balanceDue
                                  : (player.payment_amount || tournament.initial_payment_amount || 0)
                              );

                              return (
                                <div key={`${player.player_id}-${player.age_category}`} className="flex flex-wrap items-center gap-2 bg-white rounded-lg border p-2 lg:p-3">
                                  <Badge variant="secondary" className="text-xs shrink-0">
                                    {player.age_category ? player.age_category.toUpperCase() : 'OPEN'}
                                  </Badge>
                                  <span className="text-sm font-medium truncate">{player.player_name}</span>
                                  {isPaid && (
                                    <Badge className="bg-green-100 text-green-700 text-[10px] shrink-0">Paid</Badge>
                                  )}
                                  {isPartial && (
                                    <>
                                      <Badge className="bg-amber-100 text-amber-700 text-[10px] shrink-0" data-testid={`tournament-partial-badge-${player.registration_id || player.player_id}`}>
                                        Partial · {cs()}{balanceDue.toFixed(2)} due
                                      </Badge>
                                    </>
                                  )}
                                  {needsPayment && !isPartial && (
                                    <Badge className="bg-orange-100 text-orange-700 text-[10px] shrink-0">Payment Pending</Badge>
                                  )}
                                  {player.status === 'cancelled' && (
                                    <Badge className="bg-red-100 text-red-700 text-[10px] shrink-0">Cancelled</Badge>
                                  )}
                                  {(needsPayment || isPartial) && player.registration_id && fee > 0 && (
                                    <div className="ml-auto">
                                      <PayWithCreditButton
                                        entityType="tournament_registration"
                                        entityId={player.registration_id}
                                        amount={fee}
                                        description={`${player.player_name} — ${tournament.name}`}
                                        buttonText={isPartial ? `Pay ${cs()}${balanceDue.toFixed(2)} due with credit` : 'Pay With Credit'}
                                        testId={`pay-with-credit-tournament-${player.registration_id}`}
                                        onSuccess={() => fetchData?.()}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Games Schedule */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              Game Schedule
                            </h4>
                          </div>
                          
                          {/* Per-child games-played summary (click a card to filter) */}
                          {(() => {
                            const stats = getPlayerGameStats(tournament);
                            if (stats.length === 0) return null;
                            return (
                              <div className="mb-4" data-testid={`games-played-summary-${tournament.id}`}>
                                <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Games Played</h4>
                                <div className="flex flex-wrap gap-2">
                                  {stats.map(s => {
                                    const active = gamePlayerFilter === s.player_id;
                                    return (
                                      <button
                                        key={s.player_id}
                                        onClick={() => setGamePlayerFilter(active ? 'all' : s.player_id)}
                                        className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${active ? 'border-emerald-400 bg-emerald-50 ring-1 ring-emerald-300' : 'bg-white hover:bg-slate-50'}`}
                                        data-testid={`games-played-chip-${s.player_id}`}
                                      >
                                        <Trophy className={`h-4 w-4 shrink-0 ${active ? 'text-emerald-600' : 'text-slate-400'}`} />
                                        <span className="text-sm leading-tight">
                                          <span className="font-semibold text-slate-800">{s.player_name}</span>
                                          <span className="text-slate-500"> played </span>
                                          <span className="font-bold text-emerald-700" data-testid={`games-played-count-${s.player_id}`}>{s.played}</span>
                                          <span className="text-slate-500"> of {s.total} game{s.total === 1 ? '' : 's'}</span>
                                        </span>
                                      </button>
                                    );
                                  })}
                                </div>
                                {gamePlayerFilter !== 'all' && (
                                  <p className="text-xs text-slate-500 mt-1.5" data-testid="games-played-filter-note">
                                    Showing games for the selected player across the schedule and completed games. Tap the highlighted card again to show all.
                                  </p>
                                )}
                              </div>
                            );
                          })()}

                          {/* Filters */}
                          <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-white rounded-lg border">
                            <Filter className="h-4 w-4 text-slate-400" />
                            
                            {/* Team Filter */}
                            {(() => {
                              const teams = [...new Set(tournament.games?.map(g => g.team_name).filter(Boolean))].sort((a, b) => a.localeCompare(b));
                              if (teams.length > 0) {
                                return (
                                  <Select value={gameTeamFilter} onValueChange={setGameTeamFilter}>
                                    <SelectTrigger className="w-[160px] h-8 text-sm" data-testid="game-team-filter">
                                      <SelectValue placeholder="All Teams" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="all">All Teams</SelectItem>
                                      {teams.map(team => (
                                        <SelectItem key={team} value={team}>{team}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                );
                              }
                              return null;
                            })()}
                            
                            {/* Age Category Filter */}
                            {(() => {
                              const ages = [...new Set(tournament.games?.map(g => g.age_category).filter(Boolean))].sort((a, b) => a.localeCompare(b));
                              if (ages.length > 1) {
                                return (
                                  <Select value={gameAgeFilter} onValueChange={setGameAgeFilter}>
                                    <SelectTrigger className="w-[120px] h-8 text-sm" data-testid="game-age-filter">
                                      <SelectValue placeholder="All Ages" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="all">All Ages</SelectItem>
                                      {ages.map(age => (
                                        <SelectItem key={age} value={age}>{age.toUpperCase()}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                );
                              }
                              return null;
                            })()}
                            
                            {/* Player Filter */}
                            {tournament.registered_players?.length > 0 && (
                              <Select value={gamePlayerFilter} onValueChange={setGamePlayerFilter}>
                                <SelectTrigger className="w-[150px] h-8 text-sm" data-testid="game-player-filter">
                                  <SelectValue placeholder="All Players" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">All Players</SelectItem>
                                  {tournament.registered_players
                                    .filter((p, i, arr) => arr.findIndex(x => x.player_id === p.player_id) === i)
                                    .map(player => (
                                    <SelectItem key={player.player_id} value={player.player_id}>
                                      {player.player_name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                            
                            {/* Clear Filters */}
                            {(gameTeamFilter !== 'all' || gameAgeFilter !== 'all' || gamePlayerFilter !== 'all') && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 text-xs"
                                onClick={() => {
                                  setGameTeamFilter('all');
                                  setGameAgeFilter('all');
                                  setGamePlayerFilter('all');
                                }}
                              >
                                Clear
                              </Button>
                            )}
                          </div>
                          
                          {(!tournament.games || tournament.games.length === 0) ? (
                            <p className="text-slate-500 text-sm py-4">No games scheduled yet</p>
                          ) : (
                            <div className="space-y-3">
                              {tournament.games
                                .filter(game => {
                                  if (!gameMatchesFilters(game, tournament)) return false;
                                  // Upcoming schedule excludes completed/archived (shown in the section below)
                                  if (game.status === 'completed' || game.is_archived) return false;
                                  return true;
                                })
                                .sort((a, b) => new Date(a.game_date) - new Date(b.game_date))
                                .map(game => (
                                <div 
                                  key={game.id} 
                                  className="bg-white rounded-lg border p-3 lg:p-4 shadow-sm"
                                >
                                  {/* Mobile-optimized game card layout */}
                                  <div className="flex flex-col gap-2 sm:gap-3">
                                    <div className="flex items-start gap-2 lg:gap-3">
                                      {/* Game Type Icon */}
                                      <div className={`p-1.5 lg:p-2 rounded-lg shrink-0 ${
                                        game.game_type === 'home' ? 'bg-green-100' : 
                                        game.game_type === 'away' ? 'bg-blue-100' : 'bg-purple-100'
                                      }`}>
                                        {game.game_type === 'home' ? (
                                          <Home className="h-4 w-4 lg:h-5 lg:w-5 text-green-600" />
                                        ) : game.game_type === 'away' ? (
                                          <Plane className="h-4 w-4 lg:h-5 lg:w-5 text-blue-600" />
                                        ) : (
                                          <Users className="h-4 w-4 lg:h-5 lg:w-5 text-purple-600" />
                                        )}
                                      </div>
                                      
                                      {/* Game Info */}
                                      <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <span className="font-semibold text-sm lg:text-base">
                                            {game.team_name || 'TBD'} vs {game.opponent_team || 'TBD'}
                                          </span>
                                          <Badge variant="outline" className="uppercase text-[10px] lg:text-xs">
                                            {game.age_category}
                                          </Badge>
                                          {game.division && (
                                            <Badge variant="secondary" className="text-[10px] lg:text-xs">
                                              {game.division}
                                            </Badge>
                                          )}
                                        </div>
                                        
                                        {/* Date, Time, Location */}
                                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-xs lg:text-sm text-slate-600">
                                          <span className="flex items-center gap-1 whitespace-nowrap">
                                            <Calendar className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                                            <span className="font-medium">{formatGameDate(game.game_date)}</span>
                                          </span>
                                          <span className="flex items-center gap-1 whitespace-nowrap">
                                            <Clock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                                            {game.start_time}
                                            {game.reporting_time && (
                                              <span className="text-[10px] text-slate-400">(Report: {game.reporting_time})</span>
                                            )}
                                          </span>
                                          {game.location && (
                                            <span className="flex items-center gap-1">
                                              <LocationLink location={game.location} locationUrl={game.location_url} className="text-xs lg:text-sm" />
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* RSVP Status & Actions */}
                                    <div className="flex items-center justify-between gap-2 pl-8 lg:pl-11">
                                      {/* Current RSVP Status for each registered player */}
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        {tournament.registered_players
                                          ?.filter(p => p.age_category === game.age_category)
                                          .map(player => {
                                            const rsvp = getPlayerRsvpForGame(tournament, game, player.player_id);
                                            return (
                                              <div 
                                                key={`${player.registration_id || player.player_id}-${game.id}`}
                                                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                                                  rsvp ? RSVP_COLORS[rsvp] : 'bg-slate-100 text-slate-500'
                                                }`}
                                                title={`${player.player_name}: ${rsvp || 'No response'}`}
                                              >
                                                {rsvp ? getRsvpIcon(rsvp) : <HelpCircle className="h-3 w-3" />}
                                                <span className="hidden sm:inline">{player.player_name.split(' ')[0]}</span>
                                              </div>
                                            );
                                          })
                                        }
                                      </div>
                                      
                                      {/* Actions */}
                                      <div className="flex gap-1.5 shrink-0">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-7 text-xs lg:h-8 lg:text-sm px-2 lg:px-3"
                                          onClick={() => viewAllRsvps(tournament, game)}
                                          data-testid={`view-all-rsvps-${game.id}`}
                                        >
                                          <span className="hidden sm:inline">View All</span>
                                          <span className="sm:hidden">All</span>
                                        </Button>
                                        <Button
                                          size="sm"
                                          className="h-7 text-xs lg:h-8 lg:text-sm px-3 lg:px-4"
                                          onClick={() => openRsvpDialog(tournament, game)}
                                          data-testid={`rsvp-btn-${game.id}`}
                                        >
                                          RSVP
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {game.notes && (
                                    <p className="text-sm text-slate-500 mt-3 pl-14">{game.notes}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Completed Games Section */}
                        {(() => {
                          const completedGames = (tournament.games || []).filter(g => (g.status === 'completed' || g.is_archived) && gameMatchesFilters(g, tournament)) || [];
                          if (completedGames.length === 0) return null;
                          
                          return (
                            <div className="mt-6 pt-6 border-t">
                              <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                                <Archive className="h-4 w-4" />
                                Completed Games ({completedGames.length})
                              </h4>
                              <div className="space-y-2">
                                {completedGames
                                  .sort((a, b) => new Date(b.game_date) - new Date(a.game_date))
                                  .map(game => (
                                    <div 
                                      key={game.id} 
                                      className="bg-slate-100 rounded-lg border border-slate-200 p-3 opacity-75"
                                    >
                                      <div className="flex items-center justify-between flex-wrap gap-2">
                                        <div className="flex items-center gap-3">
                                          <div className="p-1.5 bg-slate-200 rounded">
                                            <CheckCircle2 className="h-4 w-4 text-slate-500" />
                                          </div>
                                          <div>
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <span className="font-medium text-sm text-slate-700">
                                                {game.team_name || 'TBD'} vs {game.opponent_team || 'TBD'}
                                              </span>
                                              <Badge variant="outline" className="uppercase text-xs">
                                                {game.age_category}
                                              </Badge>
                                              {gamePlayerFilter !== 'all' && (game.selected_players || []).includes(gamePlayerFilter) && (
                                                <Badge className="bg-emerald-100 text-emerald-700 text-[10px]" data-testid={`played-badge-${game.id}`}>
                                                  ✓ Played
                                                </Badge>
                                              )}
                                            </div>
                                            <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                                              <span>{formatGameDate(game.game_date)}</span>
                                              <span>{game.start_time}</span>
                                              {game.location && (
                                                <LocationLink location={game.location} locationUrl={game.location_url} />
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        <Badge className="bg-slate-200 text-slate-600 text-xs">
                                          Completed
                                        </Badge>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          );
                        })()}
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Teams Tab */}
          <TabsContent value="my-teams" className="mt-6">
            {loadingTeams ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : myTeams.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Your children haven't been assigned to any tournament teams yet.</p>
                  <p className="text-sm text-slate-400 mt-2">Teams are assigned by coaches and admins after tournament registration.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {myTeams.map(team => (
                  <Card key={team.team_id} className="overflow-hidden">
                    <CardHeader 
                      className="cursor-pointer hover:bg-slate-50 transition-colors p-3 lg:p-6"
                      onClick={() => setExpandedTeam(expandedTeam === team.team_id ? null : team.team_id)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <CardTitle className="flex items-center gap-1.5 text-base lg:text-lg">
                            <Users className="h-4 w-4 lg:h-5 lg:w-5 text-blue-600 shrink-0" />
                            <span className="truncate">{team.team_name}</span>
                          </CardTitle>
                          <p className="text-xs lg:text-sm text-slate-500 mt-0.5 truncate">
                            {team.tournament_name} • {team.age_category?.toUpperCase()}
                            {team.division && ` • ${team.division === 'div_a' ? 'Div A' : 'Div B'}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Badge variant="secondary" className="text-xs whitespace-nowrap">
                            {team.player_count} players
                          </Badge>
                          {expandedTeam === team.team_id ? (
                            <ChevronUp className="h-4 w-4 text-slate-400" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-slate-400" />
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs lg:text-sm text-slate-500">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs max-w-[200px] truncate">
                          {team.my_children.join(', ')}
                        </Badge>
                        <span className="flex items-center gap-1 whitespace-nowrap">
                          <Calendar className="h-3.5 w-3.5 shrink-0" />
                          {team.tournament_dates}
                        </span>
                        <span className="flex items-center gap-1 whitespace-nowrap">
                          <Users className="h-3.5 w-3.5 shrink-0" />
                          {team.coach_name}
                        </span>
                        {team.is_locked && (
                          <Badge variant="outline" className="text-[10px]">Locked</Badge>
                        )}
                      </div>
                    </CardHeader>

                    {expandedTeam === team.team_id && (
                      <CardContent className="border-t bg-slate-50">
                        <h4 className="font-medium text-sm text-slate-700 mb-3">Team Roster</h4>
                        <div className="grid gap-2">
                          {team.players.map((player, idx) => (
                            <div 
                              key={`${player.player_id}-${idx}`}
                              className={`flex items-center justify-between p-3 rounded-lg ${
                                player.is_my_child 
                                  ? 'bg-blue-50 border border-blue-200' 
                                  : 'bg-white border border-slate-200'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-sm font-medium text-slate-400 w-6">
                                  {idx + 1}.
                                </span>
                                <div>
                                  <p className={`font-medium ${player.is_my_child ? 'text-blue-700' : 'text-slate-700'}`}>
                                    {player.player_name}
                                    {player.is_my_child && (
                                      <Badge variant="outline" className="ml-2 text-xs bg-blue-100 border-blue-200">
                                        Your Child
                                      </Badge>
                                    )}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {player.skill_level && `${player.skill_level.charAt(0).toUpperCase() + player.skill_level.slice(1)}`}
                                    {player.skill_level && player.gender && ' • '}
                                    {player.gender && `${player.gender.charAt(0).toUpperCase() + player.gender.slice(1)}`}
                                  </p>
                                </div>
                              </div>
                              {player.shirt_number && (
                                <Badge variant="outline" className="text-sm">
                                  #{player.shirt_number}
                                </Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Team Chats Tab */}
          <TabsContent value="chats" className="mt-6">
            {selectedTeamChat ? (
              <TeamChat 
                teamId={selectedTeamChat.team_id}
                teamName={selectedTeamChat.team_name}
                onBack={() => {
                  setSelectedTeamChat(null);
                  fetchTeamChats(); // Refresh to update unread counts
                }}
              />
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageCircle className="h-5 w-5" />
                    Team Chats
                  </CardTitle>
                  <CardDescription>
                    Chat with coaches and other parents in your team
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingChats ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                    </div>
                  ) : teamChats.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageCircle className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <h3 className="text-lg font-medium text-slate-900">No Team Chats</h3>
                      <p className="text-slate-500 mt-1">
                        You'll see chats here once your child is assigned to a tournament team.
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {teamChats.map(chat => (
                        <div 
                          key={chat.team_id}
                          className="py-3 lg:py-4 cursor-pointer hover:bg-slate-50 -mx-4 lg:-mx-6 px-4 lg:px-6 transition-colors"
                          onClick={() => setSelectedTeamChat(chat)}
                          data-testid={`chat-room-${chat.team_id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <h4 className="font-semibold text-sm lg:text-base">{chat.team_name}</h4>
                                <Badge variant="secondary" className="text-[10px] lg:text-xs">
                                  {chat.age_category?.toUpperCase()}
                                </Badge>
                                {chat.unread_count > 0 && (
                                  <Badge className="bg-red-500 text-white text-[10px] lg:text-xs">
                                    {chat.unread_count} new
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs lg:text-sm text-slate-500 truncate">
                                {chat.tournament_name}
                              </p>
                              {chat.last_message && (
                                <p className="text-xs lg:text-sm text-slate-600 mt-1 truncate">
                                  <span className="font-medium">{chat.last_message.sender_name}:</span>{' '}
                                  {chat.last_message.content}
                                </p>
                              )}
                            </div>
                            <div className="text-right text-xs lg:text-sm text-slate-500 shrink-0">
                              <p className="flex items-center gap-1 justify-end">
                                <Users className="h-3.5 w-3.5 shrink-0" />
                                {chat.participant_count}
                              </p>
                              {chat.last_message && (
                                <p className="mt-0.5 whitespace-nowrap">
                                  {new Date(chat.last_message.created_at).toLocaleDateString('en-US', { timeZone: businessTz })}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Register Dialog */}
        <ParentTournamentRegisterDialog
          open={showRegisterDialog}
          onOpenChange={setShowRegisterDialog}
          selectedTournament={selectedTournament}
          formData={formData}
          setFormData={setFormData}
          eligibilityInfo={eligibilityInfo}
          locations={locations}
          getUnregisteredPlayers={getUnregisteredPlayers}
          getAvailableCategoriesForPlayer={getAvailableCategoriesForPlayer}
          getEligibilityForCategory={getEligibilityForCategory}
          getPlayUpCategory={getPlayUpCategory}
          handlePlayerSelect={handlePlayerSelect}
          handleRegister={handleRegister}
        />

        {/* RSVP Dialog */}
        <Dialog open={showRsvpDialog} onOpenChange={setShowRsvpDialog}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>RSVP for Game</DialogTitle>
              <DialogDescription>
                vs {selectedGame?.opponent_team} on {selectedGame?.game_date ? formatGameDate(selectedGame.game_date) : ''} at {selectedGame?.start_time}
              </DialogDescription>
            </DialogHeader>

            <div className="py-4 space-y-4">
              {(() => {
                // Match the backend's RSVP eligibility rule (see
                // routers/tournaments/games.py:submit_game_rsvp).
                // A player is eligible if EITHER:
                //   (a) their registration's age_category is in the
                //       game's age_categories (case-insensitive), OR
                //   (b) they're currently on a team whose age_category
                //       is in the game's age_categories (again case-
                //       insensitive) — this covers internal games where
                //       the team spans a different bucket than the
                //       registration's primary.
                const gameCats = (
                  selectedGame?.age_categories?.length
                    ? selectedGame.age_categories
                    : [selectedGame?.age_category]
                )
                  .filter(Boolean)
                  .map((c) => String(c).toLowerCase());

                const matchesCategory = (val) =>
                  val && gameCats.includes(String(val).toLowerCase());

                const eligiblePlayers = (selectedTournament?.registered_players || [])
                  .filter((p) =>
                    p.on_team === true &&
                    (matchesCategory(p.age_category) || matchesCategory(p.team_age_category))
                  );
                
                if (eligiblePlayers.length === 0) {
                  return (
                    <div className="text-center py-6 text-slate-500">
                      <Users className="h-10 w-10 mx-auto mb-2 opacity-50" />
                      <p className="font-medium">No players on a team yet</p>
                      <p className="text-sm mt-1">Your registered players need to be assigned to a team before you can RSVP for games.</p>
                    </div>
                  );
                }
                
                return eligiblePlayers.map((player, idx) => (
                  <div key={`${player.player_id}-${idx}`} className="space-y-2">
                    <label className="text-sm font-medium">
                      {player.player_name}
                      {player.team_name && (
                        <span className="text-slate-500 font-normal ml-2">({player.team_name})</span>
                      )}
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {RSVP_OPTIONS.map(option => {
                        const Icon = option.icon;
                        const isSelected = rsvpSelections[player.player_id] === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => setRsvpSelections({
                              ...rsvpSelections,
                              [player.player_id]: option.value
                            })}
                            className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all ${
                              isSelected 
                                ? `${option.bgColor} border-current ${option.color}`
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                            data-testid={`rsvp-${player.player_id}-${option.value}`}
                          >
                            <Icon className={`h-5 w-5 ${isSelected ? option.color : 'text-slate-400'}`} />
                            <span className={`text-xs font-medium ${isSelected ? '' : 'text-slate-500'}`}>
                              {option.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ));
              })()}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRsvpDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleRsvpSubmit} 
                disabled={submittingRsvp || Object.keys(rsvpSelections).length === 0}
                data-testid="submit-rsvp-btn"
              >
                {submittingRsvp && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Submit RSVP
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View All RSVPs Dialog */}
        <Dialog open={showAllRsvpsDialog} onOpenChange={setShowAllRsvpsDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>All RSVPs</DialogTitle>
              <DialogDescription>
                vs {selectedGame?.opponent_team} • {selectedGame?.game_date ? formatGameDate(selectedGame.game_date) : ''} • {selectedGame?.age_category?.toUpperCase()}
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              {loadingRsvps ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
                </div>
              ) : allRsvps.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No RSVPs submitted yet</p>
              ) : (
                <div className="space-y-3">
                  {/* Summary */}
                  <div className="flex gap-4 justify-center mb-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="font-medium">{allRsvps.filter(r => r.response === 'yes').length}</span>
                      <span className="text-slate-500 text-sm">Yes</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <XCircle className="h-5 w-5 text-red-600" />
                      <span className="font-medium">{allRsvps.filter(r => r.response === 'no').length}</span>
                      <span className="text-slate-500 text-sm">No</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <HelpCircle className="h-5 w-5 text-yellow-600" />
                      <span className="font-medium">{allRsvps.filter(r => r.response === 'tentative').length}</span>
                      <span className="text-slate-500 text-sm">Tentative</span>
                    </div>
                  </div>
                  
                  {/* List */}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Player</TableHead>
                        <TableHead>Response</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allRsvps.map(rsvp => (
                        <TableRow key={rsvp.id}>
                          <TableCell className="font-medium">{rsvp.player_name}</TableCell>
                          <TableCell>
                            <Badge className={RSVP_COLORS[rsvp.response]}>
                              <span className="flex items-center gap-1">
                                {getRsvpIcon(rsvp.response)}
                                {rsvp.response}
                              </span>
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Withdrawal Request Dialog */}
        <AlertDialog open={cancelRegistrationDialogOpen} onOpenChange={(open) => {
          setCancelRegistrationDialogOpen(open);
          if (!open) { setWithdrawalReason(''); setRegistrationToCancel(null); }
        }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Request Tournament Withdrawal</AlertDialogTitle>
              <AlertDialogDescription className="space-y-3">
                <span className="block">You are requesting to withdraw your child from this tournament. Your request will be reviewed by the administration.</span>
                
                <span className="block bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 text-sm">
                  <strong>Please note:</strong> If any registration fee was paid, it will be up to the administration to determine if a refund can be issued.
                </span>
                
                <span className="block">
                  <label className="text-sm font-medium text-slate-700 block mb-1">
                    Reason for withdrawal <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={withdrawalReason}
                    onChange={(e) => setWithdrawalReason(e.target.value)}
                    placeholder="Please explain why you'd like to withdraw..."
                    className="w-full rounded-md border border-slate-300 p-3 text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-900"
                    data-testid="withdrawal-reason-input"
                  />
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep Registration</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmCancelRegistration}
                className="bg-rose-500 hover:bg-rose-600"
                disabled={!withdrawalReason.trim() || submittingWithdrawal}
                data-testid="submit-withdrawal-btn"
              >
                {submittingWithdrawal ? 'Submitting...' : 'Submit Withdrawal Request'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Tournament payment confirm — Card vs ACH chooser */}
        <Dialog
          open={!!payDialog}
          onOpenChange={(o) => { if (!o) { setPayDialog(null); setPayingRegistration(null); } }}
        >
          <DialogContent className="max-w-md" data-testid="tournament-pay-dialog">
            <DialogHeader>
              <DialogTitle>Pay tournament registration</DialogTitle>
              <DialogDescription>
                {payDialog?.tournament?.name}
                {payDialog?.registration?.player_name ? ` · ${payDialog.registration.player_name}` : ''}
              </DialogDescription>
            </DialogHeader>
            <PaymentMethodChooser
              baseAmount={payDialog?.baseAmount || 0}
              value={payChoice}
              onChange={setPayChoice}
              disabled={!!payingRegistration}
            />
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setPayDialog(null)}
                disabled={!!payingRegistration}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmPayment}
                disabled={!payChoice || !!payingRegistration}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="tournament-pay-confirm-btn"
              >
                {payingRegistration ? 'Redirecting…' : 'Continue to Checkout'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ParentTournaments;
